import {
  computedLatestTrustDepositIso,
  toCalendarIsoDay,
} from "./tradeDashboardDates";
import {
  agentRowMatchesUser,
  commissionAmountForTrade,
  eftBelongsToAgent,
  formatStatDate,
  formatStatMoney,
  tradeIdFromEft,
} from "./dashboardStatsUtils";
import { getTradeFinancialReportTotals } from "./tradeFinancialUtils";

function isAgentUser(appUser) {
  return appUser?.role === "agent";
}

function canonicalEftType(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function parseEftNumber(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const rev = s.match(/^REV-(\d+)$/i);
  if (rev) return parseInt(rev[1], 10);
  const eft = s.match(/(?:EFT#?\s*)?(\d+)/i);
  if (eft) return parseInt(eft[1], 10);
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

/** Real Estate Trust 1000-series Commission Transfer → commission received. */
function isRealEstateCommissionTransfer(e) {
  if (!e || e.reversed) return false;
  const num = parseEftNumber(e.eftNumber);
  if (num == null || num < 1000 || num >= 2000) return false;
  return canonicalEftType(e.type) === "commissiontransfer";
}

function isAgentCommissionTransfer(e) {
  return canonicalEftType(e?.type) === "agentcommissiontransfer";
}

function isOurBrokerageCommission(e) {
  return canonicalEftType(e?.type) === "ourbrokeragecommission";
}

/** Paid / cheque date (when commission was paid). */
function payoutPaidIso(eft) {
  return toCalendarIsoDay(eft?.chequeDate || eft?.date || eft?.createdAt);
}

/**
 * EFT issued date: prefer record `date` when it differs from chequeDate
 * (abc Office Share stores created-at in `date`), else createdAt.
 */
function payoutIssuedIso(eft) {
  const cheque = toCalendarIsoDay(eft?.chequeDate);
  const recordDate = toCalendarIsoDay(eft?.date);
  if (recordDate && cheque && recordDate !== cheque) return recordDate;
  return (
    toCalendarIsoDay(eft?.createdAt) ||
    recordDate ||
    cheque ||
    null
  );
}

function compareIsoDesc(a, b) {
  return String(b || "").localeCompare(String(a || ""));
}

function amountsMatchWithHst(netAmount, grossAmount) {
  const net = Number(netAmount) || 0;
  const gross = Number(grossAmount) || 0;
  if (net <= 0 || gross <= 0) return false;
  const expected = Math.round(net * 1.13 * 100) / 100;
  return Math.abs(expected - gross) < 0.05 || Math.abs(net - gross) < 0.05;
}

/**
 * Commission received for Trade Status: EFT / progress tracker only —
 * never fall back to trust deposit (that is shown separately).
 */
export function getTradeStatusCommissionReceivedIso(trade) {
  const eftIso = String(trade?.paymentReceivedEftDateIso || "").trim();
  if (eftIso) return toCalendarIsoDay(eftIso) || eftIso;
  const stored =
    trade?.progressTracker?.paymentReceivedDate ||
    trade?.keyInfo?.paymentReceivedDate ||
    "";
  return toCalendarIsoDay(stored) || null;
}

/**
 * Enrich trades with commission-received dates from RE Commission Transfer EFTs.
 */
export function enrichTradesForAgentTradeStatus(
  trades,
  commissionTrustEfts = [],
  realEstateTrustEfts = [],
) {
  const receivedByTrade = new Map();
  const receivedAmountByTrade = new Map();

  const bumpReceived = (tid, iso, amount) => {
    if (!tid || !iso) return;
    const prev = receivedByTrade.get(tid);
    // Prefer earliest commission-received date (matches progress report)
    if (!prev || iso < prev) receivedByTrade.set(tid, iso);
    const n = Number(amount) || 0;
    if (n > 0) {
      const prevAmt = receivedAmountByTrade.get(tid) || 0;
      // Keep largest single receipt — avoid double-counting RE + CT mirror of same transfer
      if (n > prevAmt) receivedAmountByTrade.set(tid, n);
    }
  };

  for (const e of realEstateTrustEfts || []) {
    if (!isRealEstateCommissionTransfer(e)) continue;
    const tid = tradeIdFromEft(e);
    const iso = payoutPaidIso(e);
    bumpReceived(tid, iso, e.amount);
  }

  for (const e of commissionTrustEfts || []) {
    if (e?.reversed) continue;
    const tid = tradeIdFromEft(e);
    if (!tid) continue;
    const t = canonicalEftType(e.type);
    // CT mirror of commission transfer / received (not payouts)
    if (
      t === "commissiontransfer" ||
      t === "commissionreceived" ||
      (t === "" && parseEftNumber(e.eftNumber) >= 4000)
    ) {
      bumpReceived(tid, payoutPaidIso(e), e.amount);
    }
  }

  return (trades || []).map((trade) => {
    const tid = String(trade?._id || "");
    const fromEft = receivedByTrade.get(tid) || "";
    const existing = String(trade?.paymentReceivedEftDateIso || "").trim();
    const amountFromEft = receivedAmountByTrade.get(tid);
    return {
      ...trade,
      paymentReceivedEftDateIso: fromEft || existing,
      paymentReceivedEftAmount:
        amountFromEft > 0
          ? amountFromEft
          : Number(trade?.paymentReceivedEftAmount) || 0,
    };
  });
}

export function agentPayoutsForTrade(trade, efts, appUser) {
  const tradeId = String(trade?._id || "");
  if (!tradeId) return [];

  const tradeEfts = (efts || []).filter(
    (eft) => !eft?.reversed && tradeIdFromEft(eft) === tradeId,
  );

  const agentTransfers = tradeEfts
    .filter((eft) => isAgentCommissionTransfer(eft))
    .filter((eft) =>
      isAgentUser(appUser)
        ? eftBelongsToAgent(eft, appUser, new Set([tradeId]))
        : true,
    );

  const agentDue = commissionAmountForTrade(
    trade,
    isAgentUser(appUser) ? appUser : null,
  );

  // Office Share (Our Brokerage Commission) is the agent payout when the
  // agent's net + HST was transferred as office share (no AgentCommissionTransfer).
  let officeShare = [];
  if (agentTransfers.length === 0) {
    const matchingAgents = (trade?.agentCommissionList || []).filter((row) =>
      isAgentUser(appUser) ? agentRowMatchesUser(row, appUser) : true,
    );
    officeShare = tradeEfts.filter((eft) => {
      if (!isOurBrokerageCommission(eft)) return false;
      if (!isAgentUser(appUser)) return true;
      if (matchingAgents.length === 0) return false;
      // Typical case: $1,600 net + 13% HST = $1,808 Office Share
      if (amountsMatchWithHst(agentDue, eft.amount)) return true;
      // Single matching agent on the trade — Office Share is their payout
      return matchingAgents.length === 1;
    });
  }

  const matched = [...agentTransfers, ...officeShare];

  return matched
    .map((eft) => {
      const paidIso = payoutPaidIso(eft);
      const issuedIso = payoutIssuedIso(eft);
      return {
        id: String(eft._id || eft.eftNumber || `${tradeId}-${eft.date}`),
        eftNumber:
          eft.eftNumber != null && String(eft.eftNumber).trim() !== ""
            ? String(eft.eftNumber).trim()
            : "",
        amount: Number(eft.amount) || 0,
        dateIso: paidIso,
        date: paidIso ? formatStatDate(paidIso) : "",
        issuedIso,
        issuedDate:
          issuedIso && issuedIso !== paidIso ? formatStatDate(issuedIso) : "",
        agentName: String(eft.agentName || eft.recipient || "").trim(),
        type: String(eft.type || ""),
      };
    })
    .sort((a, b) => compareIsoDesc(a.dateIso, b.dateIso));
}

function formatDealStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "—";
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function normalizedComplianceValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function isTradeComplianceComplete(trade) {
  const pt = trade?.progressTracker || {};
  const tracker = normalizedComplianceValue(pt.complianceComplete);
  if (tracker === "complete" || tracker === "yes") return true;
  const report = normalizedComplianceValue(pt.progressReportCompliance);
  if (report === "yes" || report === "complete") return true;
  return false;
}

function displayDealStatus(trade) {
  const keyStatus = String(trade?.keyInfo?.status || "").trim();
  const upper = keyStatus.toUpperCase();
  if (upper === "FALLEN THRU" || upper === "EXPIRED") {
    return formatDealStatus(keyStatus);
  }
  if (!isTradeComplianceComplete(trade)) return "Pending";
  if (upper === "CLOSED") return "Closed";
  return "Completed";
}

function invoiceIssuedDisplay(trade) {
  const pt = trade?.progressTracker || {};
  if (String(pt.commissionInvoiceReceived || "") === "Requested") {
    return "Requested";
  }
  const sent =
    pt.commissionInvoiceReceived === "Yes" ||
    pt.commissionInvoiceSentListing === "Yes" ||
    pt.commissionInvoiceSentCoop === "Yes" ||
    Boolean(pt.invoicedDate);
  if (!sent) return "";
  const iso = toCalendarIsoDay(pt.invoicedDate || pt.fileCompleteDate);
  return iso ? formatStatDate(iso) : "Yes";
}

function commissionReceivedAmount(trade) {
  const ledger = Number(trade?.paymentReceivedEftAmount) || 0;
  if (ledger > 0) return ledger;
  const totals = getTradeFinancialReportTotals(trade);
  return totals.totalCommission || totals.grossCommission || 0;
}

function formatConditionDay(raw) {
  const iso = toCalendarIsoDay(raw);
  return iso || String(raw || "").trim();
}

function formatConditions(trade) {
  const list = Array.isArray(trade?.conditions) ? trade.conditions : [];
  return list.map((c) => {
    const due = formatConditionDay(c?.dueDate);
    const met = formatConditionDay(c?.conditionMetDate);
    let suffix = "";
    if (due && met) suffix = ` — due ${due}, met ${met}`;
    else if (met) suffix = ` — met ${met}`;
    else if (due) suffix = ` — due ${due}`;
    return {
      conditionText: String(c?.conditionText || "").trim(),
      dueDate: due,
      conditionMetDate: met,
      display: `${String(c?.conditionText || "").trim()}${suffix}`,
    };
  });
}

export function buildAgentTradeStatusRow(trade, efts, appUser) {
  const key = trade?.keyInfo || {};
  const trust = Array.isArray(trade?.trustRecords) ? trade.trustRecords : [];
  const trustTotal = trust.reduce((sum, row) => {
    const marked =
      String(row?.received || "").toLowerCase() === "yes" ||
      Number(row?.amount || 0) > 0;
    return marked ? sum + (parseFloat(row.amount) || 0) : sum;
  }, 0);
  const trustDepositIso = computedLatestTrustDepositIso(trade);

  const receivedIso = getTradeStatusCommissionReceivedIso(trade);
  const receivedAmount = commissionReceivedAmount(trade);
  const agentDue = commissionAmountForTrade(
    trade,
    isAgentUser(appUser) ? appUser : null,
  );
  const payouts = agentPayoutsForTrade(trade, efts, appUser);
  const paidAmount = payouts.reduce((sum, row) => sum + row.amount, 0);
  const paidIso =
    payouts.find((row) => row.dateIso)?.dateIso ||
    String(trade?.paymentToAgentsEftDateIso || "").trim() ||
    toCalendarIsoDay(trade?.progressTracker?.agentPaidDate) ||
    "";

  const address = [key.streetNumber, key.streetName, key.unit, key.city]
    .filter(Boolean)
    .join(" ");

  const agents = (trade?.agentCommissionList || [])
    .filter((row) =>
      isAgentUser(appUser) ? agentRowMatchesUser(row, appUser) : true,
    )
    .map((row) => String(row.agentName || "").trim())
    .filter(Boolean);

  const dealStatus = displayDealStatus(trade);
  const complianceComplete = isTradeComplianceComplete(trade);
  const conditions = formatConditions(trade);

  return {
    id: trade._id,
    tradeNumber: trade.tradeNumber,
    address,
    dealStatus,
    isPending: !complianceComplete && dealStatus === "Pending",
    complianceComplete,
    agents,
    received:
      Boolean(receivedIso) || Number(trade?.paymentReceivedEftAmount) > 0,
    receivedDate: receivedIso ? formatStatDate(receivedIso) : "",
    receivedAmount,
    agentDue,
    trustTotal,
    trustDepositDate: trustDepositIso ? formatStatDate(trustDepositIso) : "",
    paidOut: paidAmount > 0 || Boolean(paidIso),
    paidDate: paidIso ? formatStatDate(paidIso) : "",
    paidAmount,
    payouts,
    invoiceDate: invoiceIssuedDisplay(trade),
    conditions,
    record: trade,
  };
}

export function formatStatusMoney(value) {
  return formatStatMoney(value);
}
